import {
  useState,
  useEffect,
  useCallback,
  useRef,
  Dispatch,
  SetStateAction
} from 'react';

interface IntersectionObserverProps {
  callback: () => void;
  option?: IntersectionObserverInit;
}

export type CallBackRef<T = HTMLElement> = Dispatch<SetStateAction<T | null>>;

function useIntersectionObserver<T extends HTMLElement>({
  callback: outerCallback,
  option
}: IntersectionObserverProps) {
  const internalCallback = useRef(outerCallback);
  const internalOptions = useRef(option);

  const [ref, callbackRef] = useState<T | null>(null);

  /*
    https://ko.reactjs.org/docs/refs-and-the-dom.html#callback-refs
    에서 제시한 방법으로 해당 DOM의 정보를 가져옵니다.
  */

  /*
    타겟이 교차하는 부분에 감지되면 callback 함수를 실행시킵니다.
  */
  const intersect = useCallback(
    (entries: IntersectionObserverEntry[], observer: IntersectionObserver) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);

          internalCallback.current();
        }
      });
    },
    []
  );

  useEffect(() => {
    internalCallback.current = outerCallback;
  }, [outerCallback]);

  /*
    target이 변경되면 새로운 target에 IntersectionObserver 객체를 할당해주는 Effect입니다.
  */
  useEffect(() => {
    let observer: IntersectionObserver;

    if (ref) {
      observer = new IntersectionObserver(intersect, {
        threshold: 0.2,
        ...internalOptions.current
      });

      observer && observer.observe(ref);

      return () => {
        if (observer) return observer.disconnect();
      };
    }
  }, [ref, intersect]);

  return {
    callbackRef
  };
}

export default useIntersectionObserver;
